import { create } from "zustand";

import { applyHyperlinksToCertifiedCitations } from "../lib/citationHyperlinker";
import { getSavedMatches, searchAndMarkMatches } from "../lib/scanner";
import {
  AnalyzeCitationItem,
  AnalyzeCitationEntry,
  AnalyzeResult,
  CitationType,
  ResolutionStatus,
  ScanningStatus,
  LawCase,
} from "../models/benchly.models";
import benchlyService from "../service/benchly.service";
import { CitationStorageService } from "../service/citationStorage.service";

interface CitationStore {
  // Citation data
  citations: AnalyzeResult[];
  isLoaded: boolean;
  isLoading: boolean;

  // Scanning state
  scanStatus: ScanningStatus;
  scanProgress: number;
  scanStatusMessage: string;
  foundCitations: number;
  scanErrorMessage: string;

  // Fuzzy scanning state
  fuzzyStatus: ScanningStatus;
  fuzzyProgress: number;
  fuzzyStatusMessage: string;
  fuzzySearchCompleted: boolean; // NEW: Track if fuzzy search was already done

  // Citation actions
  setCitations: (citations: AnalyzeResult[]) => void;
  updateCitation: (updatedCitation: AnalyzeResult) => void;
  loadCitationsFromStorage: () => Promise<boolean>;
  clearCitations: () => void;

  // Scanning actions
  startScanning: () => void;
  resetScanning: () => void;
  startFuzzyScanning: () => void;
  resetFuzzyScanning: () => void;
}

export const useCitationStore = create<CitationStore>((set, get) => ({
  // Citation data
  citations: [],
  isLoaded: false,
  isLoading: false,

  // Scanning state
  scanStatus: ScanningStatus.Idle,
  scanProgress: 0,
  scanStatusMessage: "",
  foundCitations: 0,
  scanErrorMessage: "",

  // Fuzzy scanning state
  fuzzyStatus: ScanningStatus.Idle,
  fuzzyProgress: 0,
  fuzzyStatusMessage: "",
  fuzzySearchCompleted: false, // NEW: Track if fuzzy search was already done

  // Citation actions
  setCitations: (newCitations) =>
    set({
      citations: newCitations,
      isLoaded: true,
    }),

  updateCitation: (updatedCitation) =>
    set((state) => {
      const index = state.citations.findIndex(
        (c) => c.documentReferenceId === updatedCitation.documentReferenceId
      );
      if (index !== -1) {
        const newCitations = [...state.citations];
        newCitations[index] = updatedCitation;
        return { citations: newCitations };
      }
      return state;
    }),

  loadCitationsFromStorage: async () => {
    try {
      set({ isLoading: true });
      const store = await CitationStorageService.getAllCitationResults();
      if (store.results && store.results.length > 0) {
        set({
          citations: store.results,
          isLoaded: true,
          fuzzySearchCompleted: store.fuzzySearchCompleted || false, // Load flag from XML
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load citations from storage:", error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  clearCitations: () =>
    set({
      citations: [],
      isLoaded: false,
      // Also reset scanning state
      scanStatus: ScanningStatus.Idle,
      scanProgress: 0,
      scanStatusMessage: "",
      foundCitations: 0,
      scanErrorMessage: "",
      // Also reset fuzzy scanning state
      fuzzyStatus: ScanningStatus.Idle,
      fuzzyProgress: 0,
      fuzzyStatusMessage: "",
      fuzzySearchCompleted: false, // Reset flag
    }),

  // Scanning actions
  resetScanning: () =>
    set({
      scanStatus: ScanningStatus.Idle,
      scanProgress: 0,
      scanStatusMessage: "",
      foundCitations: 0,
      scanErrorMessage: "",
      // Also reset fuzzy search when starting a new scan
      fuzzyStatus: ScanningStatus.Idle,
      fuzzyProgress: 0,
      fuzzyStatusMessage: "",
      fuzzySearchCompleted: false, // Reset flag for new scan
    }),

  startScanning: () => {
    const current = get().scanStatus;
    if (current !== ScanningStatus.Idle) {
      console.warn(`Scan already in progress (status: ${current}), ignoring startScanning()`);
      return;
    }

    // Reset fuzzy search flag when starting new scan
    set({ fuzzySearchCompleted: false });

    // Fire and forget — runs independently of React lifecycle
    performScanning(set).catch((err) => {
      console.error("Unhandled scanning error:", err);
      set({ scanStatus: ScanningStatus.Error, scanErrorMessage: "Unexpected error during scan" });
    });
  },

  // Fuzzy scanning actions
  resetFuzzyScanning: () =>
    set({
      fuzzyStatus: ScanningStatus.Idle,
      fuzzyProgress: 0,
      fuzzyStatusMessage: "",
      fuzzySearchCompleted: false, // Reset flag
    }),

  startFuzzyScanning: () => {
    const current = get().fuzzyStatus;
    const alreadyCompleted = get().fuzzySearchCompleted;

    // Check if already completed
    if (alreadyCompleted) {
      console.log("[FuzzySearch] Already completed for current citations, skipping");
      return;
    }

    if (current !== ScanningStatus.Idle) {
      console.warn(
        `Fuzzy scan already in progress (status: ${current}), ignoring startFuzzyScanning()`
      );
      return;
    }

    const citations = get().citations;
    if (citations.length === 0) {
      console.warn("No citations to perform fuzzy search on");
      return;
    }

    // Fire and forget — runs independently of React lifecycle
    performFuzzyScanning(set, get).catch((err) => {
      console.error("Unhandled fuzzy scanning error:", err);
      set({
        fuzzyStatus: ScanningStatus.Error,
        fuzzyStatusMessage: "Unexpected error during fuzzy scan",
      });
    });
  },
}));

// ─────────────────────────────────────────────────────────────────
// Scanning pipeline — runs as a standalone async flow in the store
// ─────────────────────────────────────────────────────────────────

type SetFn = (partial: Partial<CitationStore>) => void;
function getPrimaryLawCase(result: AnalyzeResult): LawCase | null {
  const resolved = result.resolvedLawCase;
  if (resolved && resolved !== "none") return resolved;

  const certified = (result.citations ?? []).find(
    (c) => c.status === ResolutionStatus.ValidatedAndCertified
  );
  return certified ?? (result.citations ?? [])[0] ?? null;
}

function isCertifiedResult(result: AnalyzeResult): boolean {
  const lc = getPrimaryLawCase(result);
  return lc?.status === ResolutionStatus.ValidatedAndCertified;
}

function toAnalyzeCitationItemFromResult(result: AnalyzeResult): AnalyzeCitationItem | null {
  const id = result.documentReferenceId;
  if (!id) return null;

  const citations: AnalyzeCitationEntry[] = (result.citations ?? []).map((lc) => ({
    identifierOnDocument: lc.identifierOnDocument,
    titleOnDocument: lc.titleOnDocument,
    yearOnDocument: undefined, // you don't have year in AnalyzeResult; ok to omit
  }));

  if (citations.length === 0) return null;

  return {
    documentReferenceId: id,
    citationType: result.citationType ?? CitationType.SingleCite,
    citations,
  };
}
async function performScanning(set: SetFn): Promise<void> {
  const timings: Record<string, number> = {};
  const totalStartTime = performance.now();

  const logTiming = (label: string, startTime: number) => {
    const duration = performance.now() - startTime;
    timings[label] = duration;
    console.log(`⏱️ [${label}] completed in ${duration.toFixed(2)}ms`);
  };

  try {
    set({
      scanStatus: ScanningStatus.Scanning,
      scanProgress: 0.01,
      scanStatusMessage: "Initializing...",
    });

    // Progress callback for searchAndMarkMatches
    const handleScanProgress = (scanProgress: number, message?: string) => {
      set({
        scanProgress: scanProgress * 0.5,
        ...(message ? { scanStatusMessage: message } : {}),
      });
    };

    // Step 1: Document scan
    console.log("📍 [Step 1] Starting document scan...");
    const step1Start = performance.now();
    const searchResult = await searchAndMarkMatches(handleScanProgress);
    logTiming("Step 1: searchAndMarkMatches", step1Start);
    console.log("Search result:", searchResult);

    set({ scanProgress: 0.5, scanStatusMessage: "Retrieving citation data..." });

    // Step 2: Get saved matches
    if (searchResult <= 0) {
      set({
        scanStatus: ScanningStatus.NoCitations,
        scanProgress: 1.0,
        foundCitations: 0,
        scanStatusMessage: "No citations found in document",
      });
      printTimingSummary(timings, totalStartTime);
      return;
    }

    console.log("📍 [Step 2] Found citations, retrieving saved matches...");
    const step2Start = performance.now();
    const matches = await getSavedMatches();
    logTiming("Step 2: getSavedMatches", step2Start);
    console.log("Saved matches:", matches);

    if (matches.length === 0) {
      set({
        scanStatus: ScanningStatus.NoCitations,
        scanProgress: 1.0,
        foundCitations: 0,
        scanStatusMessage: "No citations found in document",
      });
      printTimingSummary(timings, totalStartTime);
      return;
    }

    set({
      foundCitations: matches.length,
      scanProgress: 0.55,
      scanStatusMessage: `Found ${matches.length} citations, preparing analysis...`,
    });

    // Step 3a: Prepare citation items
    console.log("📍 [Step 3a] Preparing citation items...");
    const step3aStart = performance.now();
    const citationItems: AnalyzeCitationItem[] = matches.map((match) => {
      const id = match.bookmarkName || `citation-${Math.random()}`;

      // Use the citationType and citations directly from SavedMatch
      const citations =
        match.citations && match.citations.length > 0
          ? match.citations
          : [
              {
                identifierOnDocument: match.identifierText || match.text || "",
                titleOnDocument: match.title || undefined,
                yearOnDocument: match.year || undefined,
              },
            ];

      return {
        documentReferenceId: id,
        citationType: match.citationType || CitationType.SingleCite,
        citations,
      };
    });
    logTiming("Step 3a: Prepare citation items", step3aStart);

    if (citationItems.length === 0) {
      set({
        scanStatus: ScanningStatus.NoCitations,
        scanProgress: 1.0,
        scanStatusMessage: "No citations found in document",
      });
      printTimingSummary(timings, totalStartTime);
      return;
    }

    // ═══════════════════════════════════════════════════════════════
    // STAGE 1: EXACT SEARCH
    // ═══════════════════════════════════════════════════════════════
    set({
      scanStatus: ScanningStatus.ExactSearching,
      scanProgress: 0.55,
      scanStatusMessage: `Running exact search on ${citationItems.length} citations...`,
    });

    console.log("📍 [Step 3b] Exact search with Benchly service...");
    const step3bStart = performance.now();

    let exactResponse;
    try {
      console.log("[Exact] items:", citationItems.length);
      console.log(
        "[Exact] total entries:",
        citationItems.reduce((sum, it) => sum + (it.citations?.length ?? 0), 0)
      );
      console.log("[Exact] payload bytes:", new Blob([JSON.stringify(citationItems)]).size);

      exactResponse = await benchlyService.analyzeLawCasesExact(citationItems);
      logTiming("Step 3b: analyzeLawCasesExact API call", step3bStart);
    } catch (apiError) {
      console.error("Exact search error:", apiError);
      set({
        scanStatus: ScanningStatus.Error,
        scanProgress: 1.0,
        scanStatusMessage: "Backend service is down, please try again later",
        scanErrorMessage: "Backend service is down, please try again later",
      });
      printTimingSummary(timings, totalStartTime);
      return;
    }

    console.log("Exact results:", exactResponse.results);

    // Separate certified from non-certified for logging
    const certifiedResults = exactResponse.results.filter((r) => isCertifiedResult(r));

    console.log(
      `📊 Exact search: ${certifiedResults.length}/${exactResponse.results.length} certified`
    );

    set({
      scanProgress: 0.7,
      scanStatusMessage: "Processing results...",
    });

    // ═══════════════════════════════════════════════════════════════
    // FINALIZE: Storage + Hyperlinks
    // ═══════════════════════════════════════════════════════════════
    console.log("📍 [Steps 4-5] Running in PARALLEL...");
    const parallelStart = performance.now();

    const [hyperlinkResult, storageResult] = await Promise.all([
      (async () => {
        try {
          console.log("  📍 [Step 4] Applying hyperlinks to certified citations...");
          const step4Start = performance.now();
          await applyHyperlinksToCertifiedCitations(exactResponse.results);
          logTiming("Step 4: applyHyperlinksToCertifiedCitations", step4Start);
          return { status: "fulfilled" as const };
        } catch (error) {
          console.error("Failed to apply hyperlinks:", error);
          return { status: "rejected" as const, reason: error };
        }
      })(),
      (async () => {
        try {
          console.log("  📍 [Step 5] Storing citation results...");
          const step5Start = performance.now();
          await CitationStorageService.storeCitationResults(exactResponse.results);
          logTiming("Step 5: storeCitationResults", step5Start);
          return { status: "fulfilled" as const };
        } catch (error) {
          console.error("Failed to store results:", error);
          return { status: "rejected" as const, reason: error };
        }
      })(),
    ]);

    // Update citations with exact results
    set({ citations: exactResponse.results, isLoaded: true });

    logTiming("Steps 4-5 PARALLEL TOTAL", parallelStart);

    if (hyperlinkResult.status === "rejected") {
      console.error("Failed to apply hyperlinks:", hyperlinkResult.reason);
    }
    if (storageResult.status === "rejected") {
      console.error("Failed to store citation results:", storageResult.reason);
    }

    // ── EXACT SEARCH COMPLETE ──
    set({
      scanStatus: ScanningStatus.Complete,
      scanProgress: 1.0,
      scanStatusMessage: `Scan complete: ${certifiedResults.length} certified`,
    });
  } catch (error) {
    console.error("Scanning failed:", error);
    set({
      scanStatus: ScanningStatus.Error,
      scanProgress: 1.0,
      scanStatusMessage: "Scanning failed",
      scanErrorMessage: String(error),
    });
  } finally {
    printTimingSummary(timings, totalStartTime);
  }
}

// ─────────────────────────────────────────────────────────────────
// Fuzzy scanning pipeline — runs separately after exact search
// ─────────────────────────────────────────────────────────────────

type GetFn = () => CitationStore;

async function performFuzzyScanning(set: SetFn, get: GetFn): Promise<void> {
  const timings: Record<string, number> = {};
  const totalStartTime = performance.now();

  const logTiming = (label: string, startTime: number) => {
    const duration = performance.now() - startTime;
    timings[label] = duration;
    console.log(`⏱️ [FuzzySearch: ${label}] completed in ${duration.toFixed(2)}ms`);
  };

  try {
    // Check if fuzzy search has already been completed (from XML storage)
    const store = await CitationStorageService.getAllCitationResults();
    if (store.fuzzySearchCompleted) {
      console.log("📍 [FuzzySearch] Already completed (from XML storage), skipping.");
      set({
        fuzzyStatus: ScanningStatus.Complete,
        fuzzyProgress: 1.0,
        fuzzyStatusMessage: "Fuzzy search already completed",
        fuzzySearchCompleted: true,
      });
      return;
    }

    set({
      fuzzyStatus: ScanningStatus.FuzzySearching,
      fuzzyProgress: 0.1,
      fuzzyStatusMessage: "Preparing fuzzy search...",
    });

    // Get current citations
    const currentCitations = get().citations;
    if (currentCitations.length === 0) {
      console.warn("No citations to perform fuzzy search on");
      // Mark as completed in XML storage
      try {
        await CitationStorageService.updateFuzzySearchCompleted(true);
      } catch (error) {
        console.error("Failed to update fuzzy search flag:", error);
      }
      set({
        fuzzyStatus: ScanningStatus.Complete,
        fuzzyProgress: 1.0,
        fuzzyStatusMessage: "No citations to search",
        fuzzySearchCompleted: true, // Mark as completed even if nothing to do
      });
      return;
    }

    // Filter: Only search non-certified citations
    const nonCertifiedCitations = currentCitations.filter((c) => !isCertifiedResult(c));

    if (nonCertifiedCitations.length === 0) {
      console.log("📍 [FuzzySearch] All citations already certified, skipping fuzzy search");
      // Mark as completed in XML storage
      try {
        await CitationStorageService.updateFuzzySearchCompleted(true);
      } catch (error) {
        console.error("Failed to update fuzzy search flag:", error);
      }
      set({
        fuzzyStatus: ScanningStatus.Complete,
        fuzzyProgress: 1.0,
        fuzzyStatusMessage: "All citations already certified",
        fuzzySearchCompleted: true, // Mark as completed
      });
      printFuzzyTimingSummary(timings, totalStartTime);
      return;
    }

    console.log(
      `📍 [FuzzySearch] Starting fuzzy search for ${nonCertifiedCitations.length} non-certified citations...`
    );

    set({
      fuzzyProgress: 0.2,
      fuzzyStatusMessage: `Fuzzy search for ${nonCertifiedCitations.length} citations...`,
    });

    // Prepare citation items for fuzzy search
    const step1Start = performance.now();
    const citationItems: AnalyzeCitationItem[] = nonCertifiedCitations
      .map((r) => toAnalyzeCitationItemFromResult(r))
      .filter((x): x is AnalyzeCitationItem => Boolean(x));
    logTiming("Step 1: Prepare citation items", step1Start);

    set({
      fuzzyProgress: 0.3,
      fuzzyStatusMessage: `Searching ${citationItems.length} citations...`,
    });

    // Call fuzzy search API
    console.log("📍 [FuzzySearch Step 2] Calling fuzzy search API...");
    const step2Start = performance.now();

    let fuzzyResponse;
    try {
      fuzzyResponse = await benchlyService.analyzeLawCasesFuzzy(citationItems);
      logTiming("Step 2: analyzeLawCasesFuzzy API call", step2Start);
      console.log("Fuzzy search results:", fuzzyResponse.results);
    } catch (apiError) {
      console.error("Fuzzy search API error:", apiError);
      set({
        fuzzyStatus: ScanningStatus.Error,
        fuzzyProgress: 1.0,
        fuzzyStatusMessage: "Fuzzy search failed",
      });
      printFuzzyTimingSummary(timings, totalStartTime);
      return;
    }

    set({
      fuzzyProgress: 0.7,
      fuzzyStatusMessage: "Merging results...",
    });

    // Merge fuzzy results with current citations
    console.log("📍 [FuzzySearch Step 3] Merging results...");
    const step3Start = performance.now();

    const fuzzyResultsMap = new Map<string, AnalyzeResult>();
    fuzzyResponse.results.forEach((result) => {
      if (result.documentReferenceId) {
        fuzzyResultsMap.set(result.documentReferenceId, result);
      }
    });

    const mergedCitations = currentCitations.map((existing) => {
      const id = existing.documentReferenceId || "";
      const replacement = fuzzyResultsMap.get(id);
      if (!replacement) return existing;

      return {
        ...replacement,
        documentReferenceId: existing.documentReferenceId, // keep stable id (should already match)
        lastUpdated: new Date().toISOString(),
      };
    });

    logTiming("Step 3: Merge results", step3Start);

    set({
      fuzzyProgress: 0.85,
      fuzzyStatusMessage: "Saving results...",
    });

    // Save updated citations to storage
    console.log("📍 [FuzzySearch Step 4] Saving to storage...");
    const step4Start = performance.now();

    try {
      await CitationStorageService.storeCitationResults(mergedCitations);
      logTiming("Step 4: Save to storage", step4Start);
    } catch (storageError) {
      console.error("Failed to save fuzzy search results:", storageError);
      // Continue anyway - we'll update the context
    }

    // Update the fuzzy search completed flag in XML storage
    console.log("📍 [FuzzySearch Step 5] Updating fuzzy search flag...");
    const step5Start = performance.now();

    try {
      await CitationStorageService.updateFuzzySearchCompleted(true);
      logTiming("Step 5: Update fuzzy flag", step5Start);
    } catch (flagError) {
      console.error("Failed to update fuzzy search flag:", flagError);
      // Continue anyway
    }

    // Update context with merged results
    set({
      citations: mergedCitations,
      isLoaded: true,
    });

    // Complete
    const newCertifiedCount = mergedCitations.filter((c) => isCertifiedResult(c)).length;

    set({
      fuzzyStatus: ScanningStatus.Complete,
      fuzzyProgress: 1.0,
      fuzzyStatusMessage: `Fuzzy search complete: ${fuzzyResponse.results.length} updated`,
      fuzzySearchCompleted: true, // Mark as completed in store
    });

    console.log(
      `✅ [FuzzySearch] Complete - Updated ${fuzzyResponse.results.length} of ${nonCertifiedCitations.length} citations`
    );
    console.log(`📊 Total certified citations: ${newCertifiedCount}`);
  } catch (error) {
    console.error("Fuzzy scanning failed:", error);
    set({
      fuzzyStatus: ScanningStatus.Error,
      fuzzyProgress: 1.0,
      fuzzyStatusMessage: "Fuzzy search failed",
    });
  } finally {
    printFuzzyTimingSummary(timings, totalStartTime);
  }
}

function printFuzzyTimingSummary(timings: Record<string, number>, totalStartTime: number) {
  const totalDuration = performance.now() - totalStartTime;
  console.log("\n📊 ═══════════════════════════════════════════════════");
  console.log("📊 FUZZY SEARCH PERFORMANCE SUMMARY");
  console.log("📊 ═══════════════════════════════════════════════════");
  Object.entries(timings).forEach(([label, duration]) => {
    const percentage = ((duration / totalDuration) * 100).toFixed(1);
    const bar = "█".repeat(Math.round(Number(percentage) / 5));
    console.log(
      `📊 ${bar.padEnd(20)} ${percentage.padStart(5)}% | ${duration.toFixed(0).padStart(6)}ms | ${label}`
    );
  });
  console.log("📊 ───────────────────────────────────────────────────");
  console.log(
    `📊 TOTAL TIME: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`
  );
  console.log("📊 ═══════════════════════════════════════════════════\n");
}

function printTimingSummary(timings: Record<string, number>, totalStartTime: number) {
  const totalDuration = performance.now() - totalStartTime;
  console.log("\n📊 ═══════════════════════════════════════════════════");
  console.log("📊 SCANNING PERFORMANCE SUMMARY");
  console.log("📊 ═══════════════════════════════════════════════════");
  Object.entries(timings).forEach(([label, duration]) => {
    const percentage = ((duration / totalDuration) * 100).toFixed(1);
    const bar = "█".repeat(Math.round(Number(percentage) / 5));
    console.log(
      `📊 ${bar.padEnd(20)} ${percentage.padStart(5)}% | ${duration.toFixed(0).padStart(6)}ms | ${label}`
    );
  });
  console.log("📊 ───────────────────────────────────────────────────");
  console.log(
    `📊 TOTAL TIME: ${totalDuration.toFixed(2)}ms (${(totalDuration / 1000).toFixed(2)}s)`
  );
  console.log("📊 ═══════════════════════════════════════════════════\n");
}
